/**
 * JSON parsing and validation utilities
 */

export interface JsonValidationResult {
  isValid: boolean
  error?: string
  position?: {
    line: number
    column: number
  }
}

/**
 * Safely parse JSON string
 * @param jsonString - The JSON string to parse
 * @returns Parsed JSON or null if invalid
 */
export function safeJsonParse<T = unknown>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString) as T
  } catch {
    return null
  }
}

/**
 * Check if a string is valid JSON
 * @param jsonString - The JSON string to validate
 * @returns true if valid, false otherwise
 */
export function isValidJson(jsonString: string): boolean {
  try {
    JSON.parse(jsonString)

    return true
  } catch {
    return false
  }
}

/**
 * Validate JSON and return detailed error information
 * @param jsonString - The JSON string to validate
 * @param errorMessage - Custom error message to use
 * @returns Validation result with error details if invalid
 */
export function validateJson(jsonString: string, errorMessage?: string): JsonValidationResult {
  try {
    JSON.parse(jsonString)

    return { isValid: true }
  } catch {
    const defaultError = errorMessage || 'Invalid JSON format'

    // Try to extract position from parser error
    let line = 0
    let column = 0

    try {
      // Force JSON.parse inside Function to get a detailed error with position
      // eslint-disable-next-line no-new,no-new-func
      new Function(`JSON.parse(\`${jsonString.replace('`', '\\`')}\`)`)()
    } catch (e) {
      const msg = String((e as Error).message || '')
      const positionRegex = /position\s+(\d+)/i
      const match = positionRegex.exec(msg)

      if (match) {
        const index = Number(match[1])
        const upto = jsonString.slice(0, index)
        const lines = upto.split('\n')
        line = lines.length
        column = (lines[lines.length - 1] || '').length + 1
      }
    }

    return {
      isValid: false,
      error: defaultError,
      position: line > 0 && column > 0 ? { line, column } : undefined
    }
  }
}

/**
 * Parse JSON with error handling that returns both result and error
 * @param jsonString - The JSON string to parse
 * @returns Tuple of [data, error]
 */
export function tryParseJson<T = unknown>(jsonString: string): [T | null, Error | null] {
  try {
    const data = JSON.parse(jsonString) as T

    return [data, null]
  } catch (error) {
    return [null, error as Error]
  }
}
